/**
 * Search component module.
 * @module shared/components/Search/Search
 *
 */
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import styled, {withTheme, css} from 'styled-components/macro';
import SearchDropdown from './SearchDropdown';
import Popover from '../Popover';
import Icon from '../Icon';
import {v4 as uuid} from 'uuid';
import {fill} from '../cssUtils';
import {getHexWithOpacity} from '../../utils/colors';

const _Wrapper = styled.div`
  position: relative;
  display: ${props => props.searchOptions.length ? 'inline-flex' : 'none'};
  min-width: 120px;
  max-width: ${props => props.maxWidth || '240px'};
  ${props => props.width ? fill(props.width) : ''}
  height: 30px;
  margin: ${props => props.margin || '5px'};
  padding: 2px;
  border: 3px solid rgba(255, 65, 65, 0);
  ${props => {
    if (props.theme.neumorphic) {
      return css`
        box-shadow: 1px 1px 0 0 rgba(255, 255, 255, 0.1), -1px -1px 0 0 rgba(0, 0, 0, 0.4);
        background-image: linear-gradient(to bottom right, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2));
        background-color: ${getHexWithOpacity(props.theme.tileBackgroundColor, 1)};
      `;
    }
    if (props.theme.dark) {
      return css`
        background-color: rgba(255, 255, 255, 0.1);
      `;
    }
    return css`
        background-color: rgba(0, 0, 0, 0.1);
      `;
  }};

  border-radius: 4px;
  text-align: left;
  align-items: center;
  box-sizing: border-box;
`;

const _Flexbox = styled.div`
  display: inline-flex;
  width: ${props => (props.optionsLength >= 2) ? '27%' : '17%'};
  align-items: center;
  justify-content: ${props => props.justifyContent};
`;

const _SearchText = styled.input`
  display: inline-block;
  width: ${props => (props.optionsLength >= 2) ? '73%' : '83%'};
  height: 100%;
  padding: 0;
  border: none;
  background-color: transparent;
  color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'};
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: ${(props) => props.theme.bodyText.size};

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
  }

  &:-ms-input-placeholder {
    color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
  }

  &::-ms-input-placeholder {
    color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
  }

  &:placeholder-shown {
    height: 19px;
  }
`;

const _Divider = styled.div`
  display: inline-block;
  width: 1px;
  height: 20px;
  background-color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'};
`;

/**
 * Search component
 * @name Search
 */
const Search = props => {
  const {searchOptions, defaultSearchOption, defaultSearchValue, searchFunc, maxWidth} = props;

  const firstOptionTitle = searchOptions.length && searchOptions[0].title;
  const [searchOptionSelection, setSearchOptionSelection] = useState(defaultSearchOption || firstOptionTitle);
  const [searchValue, setSearchValue] = useState(defaultSearchValue || '');
  const [dropdownPositionInfo, setDropdownPositionInfo] = useState({});
  const searchUUID = uuid();
  const searchElementId = 'searchId-' + searchUUID;

  const searchDropdownOptions = searchOptions.map(option => (
    {
      label: option.title,
      value: option.name,
    }
  ));

  const searchOptionMap = {};

  searchOptions.forEach(option => {
    searchOptionMap[option.title] = option.name;
  });

  const keyPressHandler = e => {
    if (e.key !== 'Enter') {return;}
    searchFunc(searchOptionMap[searchOptionSelection], searchValue);
  };

  const handleWindowSize = () => {
    if (document.getElementById(searchElementId)) {
      setDropdownPositionInfo(document.getElementById(searchElementId).getBoundingClientRect());
    }
  };

  useEffect(() => {
    handleWindowSize();
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleWindowSize);
    return function cleanup() {
      window.removeEventListener('resize', handleWindowSize);
    };
  });

  return <Popover content={`Searching on ${searchOptionSelection}`} disabled={!searchOptionSelection}>
    <_Wrapper
      searchOptions={searchOptions}
      onKeyPress={keyPressHandler}
      id={searchElementId}
      maxWidth={maxWidth}
    >
      <_SearchText
        type='text'
        placeholder={searchOptionSelection ? ('Search ' + searchOptionSelection + '...') : 'Search...'}
        value={searchValue}
        onChange={e => setSearchValue(e.target.value)}
        optionsLength={searchOptions.length}
      />
      {searchValue && <Icon
        svg={'close'}
        height={'16px'}
        width={'16px'}
        margin={'5px'}
        action={() => {
          setSearchValue('');
          return searchFunc(searchOptionMap[searchOptionSelection]);
        }}
        color={'rgba(255,255,255,0.8)'}
      /> || <div style={{width: '16px'}}>&nbsp;</div>}
      <_Flexbox
        justifyContent={'flex-end'}
        optionsLength={searchOptions.length}
      >
        {(searchDropdownOptions.length > 1)
          ? <SearchDropdown
            options={searchDropdownOptions}
            onChange={e => setSearchOptionSelection(e.label)}
            dropdownPositionInfo={dropdownPositionInfo}
          /> : null}
        {(searchDropdownOptions.length > 1) ? <_Divider/> : null}
        <Icon
          svg={'magnifyingGlass'}
          action={() => searchFunc(searchOptionMap[searchOptionSelection], searchValue)}
          color={'rgba(255,255,255,0.8)'}
        />
      </_Flexbox>
    </_Wrapper>
  </Popover>;
};

Search.propTypes = {
  /**
   * the search options the user can select in a dropdown (columns, fields, groups, etc.),
   * the selection is the collection to be searched
   */
  searchOptions: PropTypes.arrayOf(PropTypes.shape(
    {
      title: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }
  )).isRequired,
  /**
   * the title of the default selected search option (if undefined, defaults the first option)
   */
  defaultSearchOption: PropTypes.string,
  /**
   * the default search value
   */
  defaultSearchValue: PropTypes.string,
  /**
   * search function
   */
  searchFunc: PropTypes.func.isRequired,
  /**
   * margin
   */
  margin: PropTypes.string,
  /**
   * width
   */
  width: PropTypes.string,
  /**
   * the max width
   */
  maxWidth: PropTypes.string,
};

export default withTheme(Search);
